<template>
  <v-container fluid class="payment-list-page pa-0">
    <Create
      :CustomerId="CustomerId"
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList()"
    />

    <v-card :loading="loading" outlined class="payment-list-card">
      <div class="payment-list-header">
        <div>
          <div class="page-title">Payment</div>
          <div class="page-subtitle">
            {{ totalItems }} payment{{ totalItems === 1 ? "" : "s" }} found
          </div>
        </div>

        <v-btn
          v-if="checkRightStatus(31) && CustomerId"
          small
          rounded
          color="primary"
          dark
          @click="add"
        >
          <v-icon left small>mdi-plus</v-icon>
          New Payment
        </v-btn>
      </div>

      <div class="payment-filter-bar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          dense
          label="Search payment"
          single-line
          hide-details
          clearable
          class="payment-search"
        />

        <v-spacer />

        <DateTimePicker
          v-if="!showAll"
          defaultRange="since60DaysAgo"
          :range="true"
          :hideTime="true"
          v-model="dateTime"
          @submit="getList()"
        />
      </div>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box">
            <div class="summary-label">List Total</div>
            <div class="summary-value">{{ total | currency }}</div>
          </div>

          <div class="summary-box">
            <div class="summary-label">Result Total</div>
            <div class="summary-value">{{ fullTotal | currency }}</div>
          </div>
        </div>

        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          :items="payments"
          :loading="loading"
          @current-items="updateTotal"
          :options.sync="options"
          :server-items-length="totalItems"
          :single-expand="singleExpand"
          :expanded.sync="expanded"
          item-key="id"
          show-expand
          :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, 100] }"
          class="payment-table"
        >
          <template v-slot:item="{ item, isExpanded, expand }">
            <tr class="pointer payment-row">
              <td class="text-left">
                <v-btn @click.stop="expand(!isExpanded)" icon small>
                  <v-icon>
                    {{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                  </v-icon>
                </v-btn>
              </td>

              <td class="text-left" @click="viewpayment(item)">
                {{ item.date | moment("MM/DD/YYYY hh:mm A") }}
              </td>

              <td class="text-left" @click="viewpayment(item)">
                <div class="font-weight-bold">{{ item.localId }}</div>
                <div v-if="item.User" class="caption grey--text">
                  {{ item.User.name }}
                </div>
              </td>

              <td
                v-if="!CustomerId"
                class="text-left"
                @click="viewProfile(item)"
              >
                <div v-if="item.Customer">
                  {{ item.Customer.name }}
                </div>
                <span v-else>Unknown Customer</span>
              </td>

              <td class="text-left" @click="viewpayment(item)">
                {{ item.note || "--" }}
              </td>

              <td class="text-right font-weight-bold" @click="viewpayment(item)">
                {{ item.total | currency }}
              </td>

              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon small>
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item v-if="!CustomerId" @click="viewProfile(item)">
                      <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item>

                    <v-divider v-if="!CustomerId" />

                    <v-list-item @click="viewpayment(item)">
                      <v-list-item-title>View Payment</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <v-card flat class="expanded-payment-card">
                <v-card-text>
                  <PaymentDetail :PaymentId="item.id" :CustomerId="CustomerId" />
                </v-card-text>
              </v-card>
            </td>
          </template>

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import paymentService from "../service";
import PaymentDetail from "./PaymentDetailList";
import { mapGetters } from "vuex";
import _ from "lodash";

const DateTimePicker = () => import("@/components/DateTimePicker");
const Create = () => import("./Create");

export default {
  props: {
    CustomerId: { default: null },
    showAll: { default: false },
    due: { default: false },
  },

  name: "payment-list",

  components: {
    DateTimePicker,
    Create,
    PaymentDetail,
  },

  data() {
    return {
      fullTotal: 0,
      total: 0,
      expanded: [],
      singleExpand: true,
      dateTime: {
        start: this.$moment()
          .startOf("days")
          .subtract(60, "days")
          .toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      createDialog: false,
      headers: [],
      payments: [],
      search: "",
      loading: true,
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["date"],
        sortDesc: [true],
      },
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
  },

  watch: {
    options: {
      handler() {
        this.getList();
      },
      deep: true,
    },

    search: _.debounce(function () {
      this.options.page = 1;
      this.getList();
    }, 400),
  },

  created() {
    this.loadHeader();
    this.getList();
    this.$events.listen("paymentListEvent", () => this.getList());
  },

  beforeDestroy() {
    this.$events.remove("paymentListEvent");
  },

  methods: {
    updateTotal(val) {
      this.total = val.reduce((sum, row) => {
        return sum + Number(row.total || 0);
      }, 0);
    },

    add() {
      this.createDialog = true;
    },

    viewProfile(val) {
      if (!val.CustomerId) return;
      this.$router.push({ path: `/customer/${val.CustomerId}/profile` });
    },

    viewpayment(payment) {
      const routeData = this.$router.resolve({
        path: `/global/payment/${payment.id}/view`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    loadHeader() {
      this.headers = [
        {
          text: "",
          value: "data-table-expand",
          sortable: false,
        },
        {
          text: this.$t("labels.date"),
          align: "left",
          value: "date",
          sortable: true,
        },
        {
          text: "Payment Number",
          align: "left",
          value: "localId",
          sortable: true,
        },
      ];

      if (!this.CustomerId) {
        this.headers.push({
          text: "Customer Name",
          value: "Customer.name",
          align: "left",
          sortable: true,
        });
      }

      this.headers.push(
        {
          text: "Note",
          align: "left",
          value: "note",
          sortable: true,
        },
        {
          text: "Total",
          value: "total",
          align: "right",
          sortable: true,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        }
      );
    },

    getList() {
      this.loading = true;

      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const offset = (page - 1) * itemsPerPage;

      const where = {
        start: this.dateTime.start,
        end: this.dateTime.end,
        limit: itemsPerPage,
        offset,
        sortBy: sortBy && sortBy.length ? sortBy[0] : "date",
        sortDesc: sortDesc && sortDesc.length ? sortDesc[0] : true,
      };

      if (this.CustomerId) {
        where.CustomerId = this.CustomerId;
      }

      if (this.search) {
        where.search = this.search;
      }

      return paymentService
        .getAll(where)
        .then((response) => {
          this.totalItems = Number(response.count || 0);

          this.payments = (response.data || []).map((payment) => {
            payment.total = (payment.PaymentDetails || []).reduce((sum, d) => {
              return sum + Number(d.amount || 0);
            }, 0);

            return payment;
          });

          this.fullTotal = this.payments.reduce((sum, row) => {
            return sum + Number(row.total || 0);
          }, 0);

          return response;
        })
        .catch((error) => {
          console.error("payment getList failed", error);
          this.payments = [];
          this.totalItems = 0;
          this.total = 0;
          this.fullTotal = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.payment-list-page {
  background: #f8fafc;
}

.payment-list-card {
  border-radius: 20px;
  background: #ffffff;
}

.payment-list-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.payment-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.payment-search {
  max-width: 340px;
  min-width: 260px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 220px));
  gap: 12px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.payment-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.payment-row:hover {
  background: #f8fafc;
}

.expanded-payment-card {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

@media (max-width: 600px) {
  .payment-search {
    max-width: 100%;
    min-width: 100%;
  }

  .summary-row {
    grid-template-columns: 1fr;
  }
}
</style>